/**
 * The phone dashboard's derived state, from one dashboard reply: the pour's
 * function flags and maturity naming, every location's health judged at
 * `refNow` (the last successful fetch, never the wall clock), the counts,
 * the Overview's sections and the same order for the Location picker.
 * Shared by the page shell and the three tabs, so they always agree.
 */
import { useMemo } from 'react';
import { orderByFunction } from '../../../utils/locationChannels';
import type {
  DashboardResponse,
  LocationStatus,
  MaturityDescriptor,
  Pour2Limit,
  PourSummary,
} from '../../../services/pours';
import { maturityMeta, resolveStrengthUnit } from '../../../utils/maturityDisplay';
import { pourFunctionFlags, type PourFunctionFlags } from '../../../utils/pourFunctionality';
import {
  locationHealthMap,
  overviewSections,
  pourCounts,
  pourPhase,
  type LocationHealth,
  type OverviewSection,
  type PourCounts,
  type PourPhaseInfo,
} from './mobileDashboardModel';

export interface DashboardModel {
  pour: PourSummary;
  locations: LocationStatus[];
  flags: PourFunctionFlags;
  meta: MaturityDescriptor;
  strengthUnit: string;
  healthById: Map<string, LocationHealth>;
  counts: PourCounts;
  phase: PourPhaseInfo;
  sections: OverviewSection[];
  /** Every location in Overview order (by monitoring function, as the
   *  Overview's tables); the Location picker steps through this order. */
  orderedLocations: LocationStatus[];
}

export const buildDashboardModel = (
  data: DashboardResponse,
  pourLimits: ReadonlyArray<Pour2Limit>,
  refNow: number,
): DashboardModel => {
  const pour = data.pour;
  const locations = data.locations ?? [];
  const healthById = locationHealthMap(data, pourLimits, refNow);
  const sections = overviewSections(locations, healthById);
  const orderedLocations = orderByFunction([...locations]).ordered;
  return {
    pour,
    locations,
    flags: pourFunctionFlags(pour?.monitoring_functions, pour?.functionality),
    meta: maturityMeta(pour),
    strengthUnit: resolveStrengthUnit(pour?.strength_unit),
    healthById,
    counts: pourCounts(healthById),
    phase: pourPhase(data, refNow),
    sections,
    orderedLocations,
  };
};

/** The model of a reply, memoised; null without one. */
export const useDashboardModel = (
  data: DashboardResponse | null | undefined,
  pourLimits: ReadonlyArray<Pour2Limit>,
  refNow: number,
): DashboardModel | null =>
  useMemo(() => (data ? buildDashboardModel(data, pourLimits, refNow) : null), [data, pourLimits, refNow]);
